import React, { useEffect, useMemo, useState } from "react";
import { ActionButton } from "../buttons/ActionButton";
import { Timeframe } from "@/lib/enums/timeframe.enum";
import { fetchFromBackend } from "@/lib/utils/api";
import {
  BacktestIndicatorOption,
  BacktestResponse,
} from "@/lib/interface/Backtest.interface";

interface BacktestModalProps {
  isOpen: boolean;
  onClose: () => void;
  pair: string;
  exchange: string;
  timeframe: Timeframe;
  indicatorOptions: BacktestIndicatorOption[];
  indicatorLoading?: boolean;
  indicatorError?: unknown;
}

export const BacktestModal: React.FC<BacktestModalProps> = ({
  isOpen,
  onClose,
  pair,
  exchange,
  timeframe,
  indicatorOptions,
  indicatorLoading = false,
  indicatorError,
}) => {
  const [selectedIndicator, setSelectedIndicator] = useState<string>("");
  const [result, setResult] = useState<BacktestResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formatTimestamp = (value: number) => {
    const ms = value > 1000000000000 ? value : value * 1000;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? "-" : date.toLocaleString();
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedIndicator && indicatorOptions.length > 0) {
      setSelectedIndicator(indicatorOptions[0].id);
    } else if (
      selectedIndicator &&
      !indicatorOptions.find((opt) => opt.id === selectedIndicator)
    ) {
      setSelectedIndicator(indicatorOptions[0]?.id ?? "");
    }
    if (indicatorOptions.length) {
      console.log("[Backtest] Dropdown options populated", indicatorOptions);
    } else {
      console.log("[Backtest] Dropdown options empty");
    }
  }, [indicatorOptions, isOpen, selectedIndicator]);

  useEffect(() => {
    if (!isOpen) {
      setResult(null);
      setError(null);
    }
  }, [isOpen]);

  const handleRun = async () => {
    if (!selectedIndicator) {
      setError("Choose an indicator to run");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFromBackend("/backtest/run", {
        method: "POST",
        body: JSON.stringify({
          pair,
          exchange,
          timeframe,
          indicatorId: selectedIndicator,
        }),
      });
      setResult(res as BacktestResponse);
    } catch (err) {
      setError("Backtest failed. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const summaryCards = useMemo(() => {
    if (!result) return null;
    const { summary } = result;
    const items = [
      {
        label: "Final Equity",
        value: `$${summary.finalEquity.toFixed(2)}`,
        accent: "text-emerald-400",
      },
      {
        label: "Total P&L",
        value: `$${summary.totalPnl.toFixed(2)}`,
        accent: summary.totalPnl >= 0 ? "text-emerald-400" : "text-rose-400",
      },
      {
        label: "Trades",
        value: summary.totalTrades,
        accent: "text-blue-300",
      },
      {
        label: "Win Rate",
        value: `${summary.winRate.toFixed(1)}%`,
        accent: "text-amber-300",
      },
      {
        label: "Avg P&L",
        value: `$${summary.averagePnl.toFixed(2)}`,
        accent: summary.averagePnl >= 0 ? "text-emerald-400" : "text-rose-400",
      },
    ];
    return (
      <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {items.map((item) => (
          <div
            key={item.label}
            className="rounded-xl bg-slate-800/80 border border-slate-700 px-3 py-2 shadow-sm"
          >
            <div className="text-xs uppercase tracking-wide text-gray-400">
              {item.label}
            </div>
            <div className={`text-lg font-semibold ${item.accent}`}>
              {item.value}
            </div>
          </div>
        ))}
      </div>
    );
  }, [result]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-2 sm:px-4">
      <div className="w-full sm:w-[95vw] md:w-[90vw] lg:w-[80vw] h-[98vh] sm:h-[80vh] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-700 bg-slate-800/70">
          <div className="text-sm sm:text-base font-semibold text-white truncate">
            Backtest — {pair} — {exchange} — {timeframe}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="sm:hidden rounded-md bg-slate-700 px-3 py-2 text-sm text-white hover:bg-slate-600"
              onClick={onClose}
              aria-label="Close backtest modal"
            >
              Close
            </button>
            <button
              className="hidden sm:inline-flex text-gray-400 hover:text-white text-xl"
              onClick={onClose}
              aria-label="Close backtest modal"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 p-3 sm:p-4 overflow-hidden flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-7 gap-3 items-end">
            <div className="col-span-1 sm:col-span-3 flex flex-col gap-1">
              <label className="text-sm text-gray-300">Indicator</label>
              <select
                value={selectedIndicator}
                onChange={(e) => setSelectedIndicator(e.target.value)}
                disabled={indicatorLoading || indicatorOptions.length === 0}
                className="h-11 rounded-md bg-slate-800 border border-slate-700 text-white px-3 w-full disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {indicatorLoading && (
                  <option value="">Loading indicators...</option>
                )}
                {!indicatorLoading && indicatorOptions.length === 0 && (
                  <option value="">No backtestable indicators</option>
                )}
                {indicatorOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-1 sm:col-span-2 flex flex-col gap-1">
              <label className="text-sm text-gray-300">Capital</label>
              <input
                type="number"
                value={result?.summary.startingCapital ?? 1000}
                disabled
                className="h-11 rounded-md bg-slate-800 border border-slate-700 text-gray-400 px-3 w-full"
              />
            </div>
            <div className="col-span-2 sm:col-span-2 flex justify-end">
              <ActionButton
                onClick={() => {
                  void handleRun();
                }}
                label={loading ? "Running..." : "Run Backtest"}
                variant="success"
                disabled={
                  loading || indicatorLoading || indicatorOptions.length === 0
                }
                className="w-full md:w-auto min-w-[150px]"
              />
            </div>
          </div>

          {Boolean(indicatorError) && (
            <div className="rounded-md border border-rose-500 bg-rose-500/10 text-rose-200 px-3 py-2 text-sm">
              Failed to load indicators. Please retry.
            </div>
          )}

          {error && (
            <div className="rounded-md border border-amber-500 bg-amber-500/10 text-amber-200 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          {result && (
            <>
              <div className="flex-shrink-0">{summaryCards}</div>
              <div className="flex-1 min-h-0 rounded-lg border border-slate-800 bg-slate-800/50 overflow-hidden flex flex-col">
                <div className="h-full flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
                    <div className="text-sm text-gray-300">
                      Trades ({result.trades.length})
                    </div>
                    <div className="text-xs text-gray-400">
                      Most recent first
                    </div>
                  </div>
                  <div className="flex-1 min-h-0 overflow-auto themed-scroll">
                    <div className="min-w-[640px]">
                      <table className="w-full text-sm text-gray-200">
                        <thead className="bg-slate-800 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Side</th>
                            <th className="px-3 py-2 text-left">Entry</th>
                            <th className="px-3 py-2 text-left">Exit</th>
                            <th className="px-3 py-2 text-left">PnL</th>
                            <th className="px-3 py-2 text-left">Opened</th>
                            <th className="px-3 py-2 text-left">Closed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...result.trades]
                            .slice()
                            .sort((a, b) => b.exitTimestamp - a.exitTimestamp)
                            .map((trade, idx) => (
                              <tr
                                key={`${trade.entryTimestamp}-${idx}`}
                                className="border-b border-slate-800/60 hover:bg-slate-800/60"
                              >
                                <td className="px-3 py-2 capitalize">
                                  {trade.side}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold text-blue-200">
                                    ${trade.entryPrice.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {trade.entryAction}
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold text-blue-200">
                                    ${trade.exitPrice.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {trade.exitAction}
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className={
                                      trade.pnl >= 0
                                        ? "text-emerald-400 font-semibold"
                                        : "text-rose-400 font-semibold"
                                    }
                                  >
                                    ${trade.pnl.toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  {formatTimestamp(trade.entryTimestamp)}
                                </td>
                                <td className="px-3 py-2">
                                  {formatTimestamp(trade.exitTimestamp)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="px-3 py-2 border-t border-slate-800 bg-slate-900 sticky bottom-0">
                    {summaryCards}
                  </div>
                </div>
              </div>
            </>
          )}

          {!result && (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              Choose an indicator and run a backtest to see results.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
